import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '@formosa/shared/api/admin';
import { resolveAdminMediaUrl } from '@formosa/shared/utils/mediaUrl';
import Pagination from '../../components/Pagination';
import GifPackEditModal from './GifPackEditModal';
import { GIF_STATUS_OPTIONS, buildGifPackPageParams, formatGifStatus } from './gifConstants';

const EMPTY_FILTERS = { status: '', name: '' };
const TABLE_COLUMNS = ['封面', '名稱', 'Access', '狀態', 'GIF數', '排序', '操作'];

export default function GifPackPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  async function loadRecords(pageNo = 1, nextFilters = filters) {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.imGifPackPageQuery(buildGifPackPageParams(pageNo, nextFilters));
      setRecords(data?.records || []);
      setPage(data?.current || pageNo);
      setTotalPages(data?.pages || 1);
      setTotal(data?.total ?? 0);
    } catch (err) {
      setError(err.message || '載入失敗');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRecords(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleSearch(event) {
    event.preventDefault();
    setMessage('');
    loadRecords(1, filters);
  }

  function handleReset() {
    const reset = { ...EMPTY_FILTERS };
    setFilters(reset);
    setMessage('');
    loadRecords(1, reset);
  }

  function openCreate() {
    setEditId(null);
    setEditOpen(true);
    setMessage('');
  }

  function openEdit(row) {
    setEditId(row.id);
    setEditOpen(true);
    setMessage('');
  }

  function handleSaved(isEdit) {
    setMessage(isEdit ? '修改成功' : '新增成功');
    loadRecords(page);
  }

  return (
    <div className="page-card">
      <div className="section-header">
        <h2>GIF 包</h2>
        <div className="section-header__actions">
          <button type="button" className="btn btn-primary" onClick={openCreate}>
            新增
          </button>
        </div>
      </div>

      <form className="filter-bar" onSubmit={handleSearch}>
        <label>
          狀態
          <select
            value={filters.status}
            onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
          >
            {GIF_STATUS_OPTIONS.map((opt) => (
              <option key={opt.value || 'all'} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          名稱
          <input
            type="text"
            value={filters.name}
            onChange={(e) => setFilters((prev) => ({ ...prev, name: e.target.value }))}
            placeholder="搜尋名稱"
          />
        </label>
        <button type="submit" className="btn btn-secondary">
          查詢
        </button>
        <button type="button" className="btn btn-link" onClick={handleReset}>
          重置
        </button>
      </form>

      {message ? <p className="form-message form-message--success">{message}</p> : null}
      {error ? <p className="form-message form-message--error">{error}</p> : null}

      <div className="table-meta">共 {total} 筆</div>

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              {TABLE_COLUMNS.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="table-empty">
                  載入中…
                </td>
              </tr>
            ) : records.length === 0 ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="table-empty">
                  無資料
                </td>
              </tr>
            ) : (
              records.map((row) => (
                <tr key={row.id}>
                  <td>
                    {row.coverObjectKey ? (
                      <img
                        src={resolveAdminMediaUrl(row.coverObjectKey)}
                        alt=""
                        style={{ width: 48, height: 48, objectFit: 'cover' }}
                      />
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>{row.name}</td>
                  <td>{row.accessType || '—'}</td>
                  <td>{formatGifStatus(row.status)}</td>
                  <td>{row.gifCount ?? 0}</td>
                  <td>{row.sort}</td>
                  <td>
                    <button type="button" className="btn btn-link" onClick={() => openEdit(row)}>
                      編輯
                    </button>
                    <button
                      type="button"
                      className="btn btn-link"
                      onClick={() =>
                        navigate(`/system/sticker-manage/gif/assets?packId=${row.id}`)
                      }
                    >
                      GIF
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={(next) => loadRecords(next)} />

      <GifPackEditModal
        open={editOpen}
        packId={editId}
        onClose={() => setEditOpen(false)}
        onSaved={handleSaved}
      />
    </div>
  );
}
